import { useCallback } from "react";
import { emptyMovieActivity } from "src/lib/filmwebid/IMovieActivity";
import { useMovieActivityOptimisticUpdater } from "src/lib/filmwebid/movieActivityMutationOptimisticUpdater";
import type { IFilmwebIdMovieIdentifier } from "src/lib/filmwebid/useMovieIdentifier";
import { getMovieIdentifierQueryParam } from "src/lib/filmwebid/useMovieIdentifier";
import { useUserApiMutation } from "src/lib/filmwebid/useUserApiMutation";
import { useUserApiQueryKey } from "src/lib/filmwebid/useUserApiQuery";

type UseFavouriteArgs = {
	movieIdentifier: IFilmwebIdMovieIdentifier | null | undefined;
	onComplete?: () => void;
}


export default function useFavourite({ movieIdentifier, onComplete }: UseFavouriteArgs): [(value: boolean) => void, boolean] {

	const queryKey = useUserApiQueryKey("/api/Movie/MovieActivity", getMovieIdentifierQueryParam(movieIdentifier));
	const optimisticUpdater = useMovieActivityOptimisticUpdater(queryKey);
	const { mutate, isPending } = useUserApiMutation("/api/Favourites", { allowAnon: false }, {
		onMutate: (variables) => optimisticUpdater(old => {
			const newValue = variables.method === "POST" ? true : false;
			return {
				...old,
				movieUserInteraction: {
					...(old?.movieUserInteraction ?? emptyMovieActivity.movieUserInteraction!),
					favourite: newValue
				},
				movieStats: {
					...(old?.movieStats ?? emptyMovieActivity.movieStats!),
					favouriteCount: newValue ? (old?.movieStats?.favouriteCount ?? 0) + 1 : (old?.movieStats?.favouriteCount ?? 0) - 1
				}
			}
		}),
		onSuccess: onComplete,
	});
	const fn = useCallback((value: boolean) => {
		if (value) {
			mutate({
				method: "POST",
				body: {
					movieIdentifier: movieIdentifier ?? null,
					source: window?.location?.hostname ?? null
				}
			});
		} else {
			mutate({
				method: "DELETE",
				params: movieIdentifier ? { [movieIdentifier.type]: movieIdentifier.value } : {}
			});
		}
	}, [movieIdentifier, mutate]);
	return [fn, isPending];
}
